import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActionMenuItem, Button, Icon, Modal, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { useAuth } from '@/hooks/useAuth';
import { paths } from '@/routes/paths';

/**
 * "Sign in as" -- support's last resort.
 *
 * Separate from AccountActions because it is a different kind of act. Those are
 * things done *to* an account and the person finds out afterwards; this is
 * becoming them.
 *
 * There is a confirmation step but no form: the dialog exists to say plainly
 * what is about to happen, because an admin surprised by where they land is an
 * admin about to act as somebody else by accident. Accountability does not rest
 * on anything typed here -- the server audits who signed in as whom, and every
 * action taken during the session carries the impersonator's id.
 *
 * `account` is `{ id, full_name, role, status }` -- the *account*, never a
 * student profile or company membership id.
 */

// Where an impersonated session lands. A company user's home is their workspace
// and a student's is their dashboard; dropping them anywhere else means the
// first thing an admin sees is a redirect they did not ask for.
const LANDING = {
  STUDENT: paths.student.dashboard,
  COMPANY: paths.company.dashboard,
};

/** "Anirban Banerjee" -> "Anirban": short enough for a button. */
function firstName(fullName = '') {
  return fullName.trim().split(/\s+/)[0] || 'user';
}

/**
 * `compact`: a small tinted "Sign in as" -- the main action on a user card.
 * `menu`: an item of the surrounding `ActionMenu` ("More").
 */
export function SignInAsAction({ account, size = 'sm', compact = false, menu = false }) {
  const { user, impersonate } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  // The same four refusals the server makes. A button whose only outcome is an
  // error is worse than no button.
  const canImpersonate =
    account?.id &&
    account.id !== user?.id &&
    account.role !== 'SUPER_ADMIN' &&
    (account.status === 'ACTIVE' || account.status === 'PENDING_VERIFICATION');
  if (!canImpersonate) return null;

  async function confirm() {
    setPending(true);
    try {
      await impersonate(account.id);
      // Nothing cached was fetched as this person. Clearing beats invalidating:
      // a half-refreshed console showing one account's data under another's
      // name is the exact confusion the banner exists to prevent.
      queryClient.clear();
      setOpen(false);
      navigate(LANDING[account.role] ?? '/', { replace: true });
    } catch (error) {
      toast.error(errorMessage(error, 'Could not sign in as this user'));
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      {menu ? (
        <ActionMenuItem
          icon="users"
          title={`See the platform exactly as ${account.full_name} does`}
          onSelect={() => setOpen(true)}
        >
          Sign in as
        </ActionMenuItem>
      ) : compact ? (
        <Button
          variant="tintBrand"
          size="sm"
          onClick={() => setOpen(true)}
          title={`See the platform exactly as ${account.full_name} does`}
        >
          <Icon name="users" size={14} />
          Sign in as
        </Button>
      ) : (
        <Button
          variant="tintBrand"
          size={size}
          onClick={() => setOpen(true)}
          title={`See the platform exactly as ${account.full_name} does`}
        >
          <Icon name="eye" size={15} />
          View as {firstName(account.full_name)}
        </Button>
      )}

      <Modal
        open={open}
        title={`Sign in as ${account.full_name}`}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={pending}>
              Cancel
            </Button>
            <Button isLoading={pending} onClick={confirm}>
              Sign in as {account.full_name}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-ink-muted text-sm">
            You will see the platform exactly as {account.full_name} does, and anything you do will
            be recorded against your own account as well as theirs. A banner stays on screen until
            you come back.
          </p>
          <p className="text-ink-muted text-sm">
            The session lasts 2 hours and cannot be extended — start again if you need longer. Your
            own session stays signed in the whole time.
          </p>
        </div>
      </Modal>
    </>
  );
}
